/* jshint esversion: 6 */

/**
 * TaskFlowService is a service class responsible for managing task flow logic, including task time setup,
 * response initialization, and handling redirects based on various conditions.
 */
class TaskFlowService {
    /**
     * Constructor for TaskFlowService.
     * @param {Object} $signals - Signal service for event handling.
     * @param {Object} $bridges - Bridge service for various utilities.
     * @param {Object} $config - Configuration settings.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $q - AngularJS promise service.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $translate - Translation service.
     * @param {Object} $location - AngularJS location service.
     * @param {Object} $route - AngularJS route service.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} TaskCountdownService - Service for managing task countdown.
     * @param {Object} TaskDataService - Service for managing task data.
     * @param {Object} FocusRecoveredService - Service for handling focus recovery.
     * @param {Object} ReadingTimeService - Service for managing reading time.
     */
    constructor($signals, $bridges, $config, $rootScope, $q, $timeout, $translate, $location, $route, $injector, TaskCountdownService, TaskDataService, FocusRecoveredService, ReadingTimeService) {
        this.$signals = $signals;
        this.$bridges = $bridges;
        this.$config = $config;
        this.$rootScope = $rootScope;
        this.$q = $q;
        this.$timeout = $timeout;
        this.$translate = $translate;
        this.$location = $location;
        this.$route = $route;
        this.$injector = $injector;
        this.TaskCountdownService = TaskCountdownService;
        this.TaskDataService = TaskDataService;
        this.FocusRecoveredService = FocusRecoveredService;
        this.ReadingTimeService = ReadingTimeService;

        this.taskCountdown = TaskCountdownService.getInstance();
        this.$logger = $bridges.logger();
        this.$eventLog = $bridges.eventLog();
        this.SERVICE = this;
        this.redirectsBound = false;
        this.responseInitialized = false;
        this.taskTimeSetup = false;
        this.isCMS = angular.isDefined($rootScope.isCMS) ? $rootScope.isCMS : false;
        this.TaskInfoService = $injector.get('TaskInfoService');
        this.InactivityTimeoutService = $injector.get('InactivityTimeoutService');
        this.HybridTimeService = null;
        this.taskInfo = this.TaskInfoService.getTaskInfo();
        this.isExaminer = false;

        if (angular.isDefined(this.taskInfo) && this.taskInfo !== null && this.taskInfo.getPackageType() === "examiner") {
            this.isExaminer = true;
        }

        this.SERVICE._logIdentifier = 'TaskFlowService';

    }

    isRouteChangeAllowedDuringExam(e, next, current) {
        console.log('TaskFlowService isRouteChangeAllowed', e, next, current);
        
        const disallowedControllers = ['InstructionsController', 'WelcomeController', 'PWAController', 'HelloUserController', 'VMDetectionController'];
        if (disallowedControllers.includes(next.$$route.controller)) {
            console.log('TaskFlowService isRouteChangeAllowed -> disallowed controller', next.$$route.controller);
            e.preventDefault();
            return false;
        }
        return true;
    }

    /**
     * Resets the task countdown.
     */
    reset() {
        this.$logger.info(this.SERVICE, 'reset');
        this.taskCountdown.stopMeasure();
    }

    /**
     * Sets up the task time for the given candidate.
     * @param {Object} candidate - The candidate for whom the task time is being set up.
     * @returns {boolean|undefined} - Returns false if the task time setup is skipped due to inactivity or CMS/Examiner mode.
     */
    setupTaskTime(candidate) {
        if (this.InactivityTimeoutService.isIdle()) {
            this.taskCountdown.recordTelemetry('taskflow.setup_task_time_skipped', 'warning', {
                reason: 'idle',
                visibilityState: document.visibilityState || 'unknown',
            });
            return false;
        }

        if (this.isCMS) {
            this.$logger.info("Not starting task time, cms mode");
            this.$signals.emit('readingTimeUnlockKeyboard');
            if (this.taskCountdown.getMode() !== 'journal') {
                this.taskCountdown.setMode("task");
            }
            return;
        } else if (this.isExaminer) {
            this.$signals.emit('readingTimeUnlockKeyboard');
            if (this.taskCountdown.getMode() !== 'journal') {
                this.taskCountdown.setMode("task");
            }
            this.$logger.info("Not starting task time, Examiner package");
            return;
        }

        var countdownNotInTaskMode = this.taskCountdown.getMode() !== 'task';

        if (countdownNotInTaskMode) {
            if (this.taskCountdown.getMode() === 'journal') {
                var _sheetUUID = this.$route.current.params.sheetUUID,
                    _taskContext = this.TaskDataService.fetchContext(),
                    _sheet = angular.isUUID(_sheetUUID) ? _taskContext.read(_sheetUUID) : null;
                if (!_sheet || (_sheet && !_sheet.isJournal())) {
                    this.$location.path('/journalOptIn/');
                }
            }
            if (this.taskCountdown.getMode() === 'readingTime') {
                this.ReadingTimeService.startReadingTime();
            }
            return;
        }

        if (this.taskTimeSetup) {
            this.$logger.info(this.SERVICE, "Task time is already setup, not doing it again");
            return;
        }

        this.$logger.info(this.SERVICE, 'setupTaskTime');

        var alreadyUsedTimeMS = 0, lastTimeDisplayMode, existingExtraTimeMs = 0;

        if (this._hadExistingData) {
            alreadyUsedTimeMS = this.$bridges.data().get('timeElapsed');
            lastTimeDisplayMode = this.$bridges.data().get('timeDisplayMode');

            if (typeof(lastTimeDisplayMode) === 'undefined') {
                lastTimeDisplayMode = 'remaining';
            }

            if (!angular.isDefined(alreadyUsedTimeMS)) alreadyUsedTimeMS = 0;

            var existingExtraTimeMsStr = this.$bridges.data().get("extraTimeTotal");
            if (angular.isDefined(existingExtraTimeMsStr) && existingExtraTimeMsStr !== null) {
                existingExtraTimeMs = parseInt(existingExtraTimeMsStr);
                if (isNaN(existingExtraTimeMs) || existingExtraTimeMs < 0) {
                    existingExtraTimeMs = 0;
                }
            }

        }

        if (this.taskCountdown && this.taskCountdown !== null && this.taskCountdown.getMode() === 'task') {
            if (existingExtraTimeMs > 0) {
                this.$logger.info(this.SERVICE, 'setupTaskTime -> setting pre-existing extra time ' + existingExtraTimeMs + 'ms');
                this.taskCountdown.setTotalExtraTimeMS(existingExtraTimeMs, false);
            } else {
                var _oneMinuteInMilliseconds = 60000,
                    IAAPreferencesService = this.$injector.get('IAAPreferencesService'),
                    candidateIAAPreferences = IAAPreferencesService.getPreferences();
                if (candidateIAAPreferences && candidateIAAPreferences.extraTime && !this.taskCountdown.isExtraTimeChanged()) {
                    this.taskCountdown.setTotalExtraTimeMS(candidateIAAPreferences.extraTime * _oneMinuteInMilliseconds, true);
                }
            }
        }

        this.$logger.info(this.SERVICE, 'setupTaskTime -> setting taskTime to ' + candidate.getTaskTime() + "ms, already used time " + alreadyUsedTimeMS + "ms");
        this.taskCountdown.resetTime(candidate.getTaskTime(), alreadyUsedTimeMS);

        if (typeof(lastTimeDisplayMode) !== 'undefined') {
            this.$logger.info(this.SERVICE, 'setting timer display mode to its last persisted state: ' + lastTimeDisplayMode);
            this.taskCountdown.setDisplayMode(lastTimeDisplayMode);
        }

        this.taskTimeSetup = true;
    }

    /**
     * Checks if there is existing data from the last result.
     * @returns {boolean} - Returns true if there is existing data, otherwise false.
     */
    hasExistingDataLastResult() {
        return this._hadExistingData;
    }

    /**
     * Gets the candidate identity from the last hasExistingData check.
     * @returns {Object|undefined} - The candidate identity object with firstName, lastName, email.
     */
    getCandidateIdentity() {
        return this._candidateIdentity;
    }

    /**
     * Session resume: the server's user-info response for specimens doesn't
     * include firstName, lastName, schoolCode or targetEmail (they're
     * form-entered, not provisioned). The stored candidate identity in
     * IndexedDB does have them, so merge them back into the current user.
     *
     * This is data preservation first and UI second. checkAndInitResponse
     * re-persists the candidate via put("candidate", user) once the response is
     * initialised, so any field missing from the merged user is written over
     * the good stored value. Restoring them here is what stops a reload from
     * destroying them; the navbar showing the right name is a side benefit.
     *
     * targetEmail is the field that must not be lost. When the packed response
     * carries none, the CMS skips creating the delivery record entirely
     * (ib-main: school_response_upload/responsefile_processor_handle_school_specimen.py),
     * so the script is stored and processed but no download link is ever sent
     * to the school. The only trace is a print in the worker log, so from the
     * app and the school's point of view the response simply vanishes. See
     * IBPIT-1968. schoolCode matters too: TaskDone uses it to build the
     * upload-complete key and the response filename.
     *
     * NOTE: this is not the only merge site. HelloUserController restores the
     * same fields from the same identity in onResumeSessionClick and
     * onContinueAsStoredCandidate when the candidate goes through the visible
     * resume screen. Any change to the merge policy needs to consider both.
     *
     * candidateNo is NOT merged here because the Candidate model only declares
     * it as `readables` (getter only). If that ever changes, add a
     * setCandidateNo branch below and include it in the CandidateIdentity wire type.
     *
     * @param {Object|undefined} identity - CandidateIdentity from hasExistingData.
     */
    mergeCandidateIdentity(identity) {
        if (!identity) {
            return;
        }
        try {
            var userInfoService = this.$injector.get('UserInfoService');
            var currentUser = userInfoService.getUserInfo();
            if (!currentUser) {
                return;
            }
            // targetEmail is merged first on purpose. It is the only field here
            // whose loss is silent and unrecoverable, and getFirstName() below is
            // unguarded, so a non-Candidate user model would throw before we ever
            // reached it if this ran last.
            if (currentUser.setTargetEmail && !currentUser.getTargetEmail() && identity.email) {
                currentUser.setTargetEmail(identity.email);
            }
            if (!currentUser.getFirstName() && identity.firstName) {
                currentUser.setFirstName(identity.firstName);
            }
            if (!currentUser.getLastName() && identity.lastName) {
                currentUser.setLastName(identity.lastName);
            }
            if (currentUser.setSchoolCode && !currentUser.getSchoolCode() && identity.schoolCode) {
                currentUser.setSchoolCode(identity.schoolCode);
            }
        } catch (e) {
            // Deliberately warn, not error: $logger.error emits the ErrorHandler
            // signal, which opens a blocking modal over a candidate mid-assessment
            // (ErrorHandlerService.onErrorSignal). A failed merge degrades the
            // session, it does not warrant stopping the exam. Sentry still gets
            // the full Error below.
            var errorDetails = (e && e.stack) ? e.stack : String(e);
            this.$logger.warn(this.SERVICE, 'Could not merge candidate identity into current user: ' + errorDetails);
            if (typeof window !== 'undefined' && window.Sentry && typeof window.Sentry.captureException === 'function') {
                window.Sentry.captureException(e, {
                    tags: { source: 'session-resume-identity-merge' },
                });
            }
        }
    }

    /**
     * Resets state to allow a fresh start-over flow.
     * Clears response initialization flags and cached data so that
     * checkAndInitResponse can run fresh for the new candidate.
     */
    resetForStartOver() {
        this.responseInitialized = false;
        this.taskTimeSetup = false;
        this._hadExistingData = false;
        this._candidateIdentity = undefined;
        this.$bridges.data().clearHasExistingDataCache();
    }

    /**
     * Ensures that the task is started.
     */
    ensureTaskIsStarted() {
        if (this.isCMS) {
            this.$logger.info(this.SERVICE, "Not starting task time, cms mode");
            return;
        } else if (this.isExaminer) {
            this.$logger.info(this.SERVICE, "Not starting task time, Examiner package");
            return;
        }

        this.$logger.info(this.SERVICE, 'ensureTaskIsStarted');

        if (this.taskCountdown.isRunning) {
            this.$logger.info(this.SERVICE, 'ensureTaskIsStarted -> already running!');
            return;
        }

        this.$logger.info(this.SERVICE, 'ensureTaskIsStarted -> starting countdown');
        this.taskCountdown.startMeasure();
    }

    /**
    readDevModeCandidateDetails(user) {
        if (this.$config.isDevMode() && user.getType() == "candidate" && !user.isIsPreRegistered()) {
            var cs = sessionStorage.candidateNonPreRegisteredDetails;
            var csp;
            try { if (angular.isDefined(cs)) csp = JSON.parse(cs); } catch (e) {}

            this.$logger.info(this.SERVICE, 'initResponse -> enriching candidate instance with data from sessionStorage', cs);

            if (angular.isDefined(csp) && angular.isDefined(csp.uuid) && csp.uuid == this.$bridges.authentication().getUserUUID()) {
                user.setFirstName(csp.firstName);
                user.setLastName(csp.lastName);
                user.setSchoolCode(csp.schoolCode);
            } else if (angular.isDefined(csp)) {
                this.$logger.info(this.SERVICE, 'initResponse -> uuid mismatch', this.$bridges.authentication().getUserUUID(), JSON.stringify(csp));
            }
        }
    }
    */

    /**
     * Generates a pirate token for the given user.
     * @param {Object} user - The user object.
     * @returns {string} - The generated pirate token.
     */
    generatePirateToken(user) {
        if (!user.isIsPirate()) {
            return "";
        }

        var payload = user.getFirstName().toLowerCase().replace(/[.-]/g, "").trim() + "-" + user.getLastName().toLowerCase().replace(/[.-]/g, "").trim(),
            hash = angular.sha256(payload);

        return hash;
    }

    clearTaskDoneUploadCompleteFlags(user) {
        try {
            var taskUUID = user && angular.isFunction(user.getTaskUUID) ? user.getTaskUUID() : null;
            if (!taskUUID || typeof sessionStorage === 'undefined') {
                return;
            }

            var cleanedUUID = taskUUID.replace(/[^a-z0-9_]/gi, '');
            var prefix = 'ib_uploadComplete_' + cleanedUUID + '_';
            var keysToRemove = [];

            for (var i = 0; i < sessionStorage.length; i++) {
                var key = sessionStorage.key(i);
                if (key && key.indexOf(prefix) === 0) {
                    keysToRemove.push(key);
                }
            }

            angular.forEach(keysToRemove, function(key) {
                sessionStorage.removeItem(key);
            });

            if (keysToRemove.length > 0) {
                this.$logger.info(this.SERVICE, 'Cleared stale taskdone upload markers', keysToRemove);
            }
        } catch (e) {
            this.$logger.warn(this.SERVICE, 'Failed to clear taskdone upload markers', e);
        }
    }

    /**
     * Checks and initializes the response for the given user.
     * @param {Object} user - The user object.
     * @param {boolean} initResponse - Flag to initialize the response.
     * @returns {Promise} - A promise that resolves when the response is initialized.
     */
    checkAndInitResponse(user, initResponse) {
        var p = this.$q.defer();
        p.message = this.$translate.instant("LOADER.CHECK-PRE-EXISTING-RESPONSE");

        if (this.isCMS) {
            p.resolve();
            return p.promise;
        }

        if (this.isExaminer) {
            this.$bridges.data().initResponse(false);
            this.$logger.info(this.SERVICE, 'checkAndInitResponse -> Examiner, not using any saved data');
            p.resolve();
            return p.promise;
        }

        if (!this.responseInitialized) {
            this.$logger.info(this.SERVICE, 'checkAndInitResponse -> setting up');

            var t = this,
                fallbackToDiskOnInitResponseFailed = false;

            var onResponseInitialised = function() {
                t.responseInitialized = true;

                // Restore timer mode from storage before enabling persistence
                // This ensures the timer can be correctly restored after session resume
                var storedMode = t.$bridges.data().get('timerMode');
                if (storedMode && (storedMode === 'task' || storedMode === 'readingTime' || storedMode === 'journal')) {
                    this.$logger.info(t.SERVICE, 'checkAndInitResponse -> restoring timer mode from storage: ' + storedMode);
                    t.taskCountdown.setMode(storedMode);
                }

                t.taskCountdown.enablePersistence();
                //t.readDevModeCandidateDetails(user);
                this.$logger.info(t.SERVICE, 'checkAndInitResponse -> updating candidate in response data store', user);
                t.$bridges.data().put("candidate", user);
                p.resolve();
            };

            var onResponseInitFailed = function() {
                if (!fallbackToDiskOnInitResponseFailed) {
                    this.$logger.info(t.SERVICE, 'checkAndInitResponse -> received initResponseFailed');
                    p.notify("Initialising response data failed");
                    p.reject("initResponseFailed");
                    return;
                }

                this.$logger.info(t.SERVICE, 'checkAndInitResponse -> received initResponseFailed for hybrid response, re-trying with blank response');
                p.notify(this.$translate.instant("LOADER.INIT-NEW-RESPONSE"));
                t.$bridges.data().initResponse(false, false);
            };

            this.$signals.on(IB.Signals.RESPONSE_INITIALIZED, onResponseInitialised.bind(this));
            this.$signals.on(IB.Signals.RESPONSE_INIT_FAILED, onResponseInitFailed.bind(this));

            var onHasExistingDataResult = function(result) {
                this.$logger.info("checkAndInitResponse -> Received hasExistingData result" +
                    " hasExistingData=" + (result.hasExistingData ? 'Y' : 'N') +
                    " hasExistingDataOnDisk=" + (result.hasExistingDataOnDisk ? 'Y' : 'N') +
                    " hasExistingDataOnWebService=" + (result.hasExistingDataOnWebService ? 'Y' : 'N') +
                    " unixTimeStampUTC=" + (result.unixTimeStampUTC)
                );

                if (angular.isDefined(result.unixTimeStampUTC)) {
                    t.HybridTimeService = t.$injector.get('HybridTimeService');
                    t.HybridTimeService.setUnixTimeStampUTC(result.unixTimeStampUTC);
                    try {
                        t.HybridTimeService.isInTimeframeWithRedirect();
                    } catch (e) {
                        var errorDetails = (e && e.stack) ? e.stack : String(e);
                        t.$logger.info(t.SERVICE, 'HybridTimeService.isInTimeframeWithRedirect() failed: ' + errorDetails);
                    }
                }

                t._hadExistingData = result.hasExistingData;
                t._candidateIdentity = result.candidateIdentity;

                // There is data to resume but nothing to restore the candidate
                // from. Whatever the current user is missing is about to be
                // persisted over the stored record by put("candidate", user)
                // below, which is how IBPIT-1968 lost 12 responses. A missing
                // identity on a fresh start is normal; on a resume it is not.
                if (result.hasExistingData && !result.candidateIdentity) {
                    t.$logger.warn(t.SERVICE,
                        'checkAndInitResponse -> existing data but no candidateIdentity to merge; ' +
                        'stored candidate fields may be overwritten');
                }

                t.mergeCandidateIdentity(t._candidateIdentity);

                if (!angular.isDefined(initResponse) || !initResponse) {
                    p.resolve();
                    return;
                }

                if (result.hasExistingData) {
                    var useHybridResponse = !result.hasExistingDataOnDisk,
                        msg;
                    if (t.taskInfo && t.taskInfo.isMediaAssessment()) {
                        useHybridResponse = false;
                    }
                    if (useHybridResponse) {
                        msg = this.$translate.instant("LOADER.INIT-PRE-EXISTING-HYBRID");
                        this.$logger.info(t.SERVICE, 'initResponse -> using pre-existing hybrid data in initResponse()');
                        var eMsg1 = this.$translate.instant("MODULES.LOGIN.EVENTLOG_RELOGIN_HYBRID");
                        this.$eventLog.logEvent(eMsg1, 'assessmentReStartHybrid', '', '', {});
                    } else {
                        msg = this.$translate.instant("LOADER.INIT-PRE-EXISTING-DISK");
                        this.$logger.info(t.SERVICE,
'initResponse -> using pre-existing data on disk in initResponse()');
                        var eMsg2 = this.$translate.instant("MODULES.LOGIN.EVENTLOG_RELOGIN_DISK");
                        this.$eventLog.logEvent(eMsg2, 'assessmentReStartDisk', '', '', {});
                    }
                    p.notify(msg);

                    t.clearTaskDoneUploadCompleteFlags(user);
                    fallbackToDiskOnInitResponseFailed = useHybridResponse;
                    this.$bridges.data().initResponse(true, useHybridResponse);
                } else {
                    p.notify(this.$translate.instant("LOADER.INIT-NEW-RESPONSE"));
                    this.$logger.info(t.SERVICE, 'initResponse -> starting with a blank response');
                    t.clearTaskDoneUploadCompleteFlags(user);
                    this.$bridges.data().initResponse(false, false);
                }
            };

            var taskInfo = this.TaskInfoService.getTaskInfo(),
                uploadConfig = taskInfo && taskInfo.getUploadConfig(),
                hybridWebServiceURL = angular.isObject(uploadConfig) ? uploadConfig.hybridWebserviceURL : false,
                hybridEnabled = hybridWebServiceURL && user.isCloudResponseUpload(),
                pirateToken = this.generatePirateToken(user);

            if (hybridEnabled) {
                console.log("checkAndInitResponse -> Using Hybrid URL: " + hybridWebServiceURL);
            }

            this.$bridges.data()
                .hasExistingData(hybridEnabled, hybridWebServiceURL, pirateToken, true)
                .then(onHasExistingDataResult.bind(this));
        } else {
            p.resolve();
        }

        return p.promise;
    }

    initRedirects($scope, $location) {
        if (this.isCMS) {
            this.$logger.info(this.SERVICE, "Not binding task redirects, cms mode");
            return;
        }
        if (this.isExaminer) {
            this.$logger.info(this.SERVICE, "Not binding task redirects, Examiner package");
            return;
        }

        if (this.redirectsBound) {
            this.$logger.info(this.SERVICE, 'initRedirects -> Already bound');
            return;
        }

        this.$logger.info(this.SERVICE, 'initRedirects -> Binding to countdownExpired');
        var onTimeExpired = () => {
            const inactivityService = this.InactivityTimeoutService;
            const focusService = this.FocusRecoveredService;
            const wasIdle = inactivityService.isIdle();

            this.taskCountdown.recordTelemetry('taskflow.countdown_expired', 'info', {
                wasIdle,
                visibilityState: document.visibilityState || 'unknown',
                destination: '/journalOptIn/',
            });

            // Dismiss inactivity modal if open — clock expiry takes precedence
            if (wasIdle) {
                this.$logger.info(this.SERVICE, 'countdownExpired -> dismissing inactivity idle state');
                inactivityService.dismissIdle();
            }

            // Always route to /journalOptIn/ — JournalOptInController acts as a
            // dispatcher: if the assessment has no journal, it redirects straight
            // to /taskdone/.
            this.$rootScope.$safeApply(() => {
                this.$location.path('/journalOptIn/');
            });
            focusService.removeNotification();
        };
        this.$signals.on('countdownExpired', onTimeExpired);

        this.$logger.info(this.SERVICE, 'initRedirects -> Binding to onFocusRecovered');

        var onTimeExpiredJournal = () => {
            const inactivityService = this.InactivityTimeoutService;
            const wasIdle = inactivityService.isIdle();

            this.taskCountdown.recordTelemetry('taskflow.countdown_expired_journal', 'info', {
                wasIdle,
                visibilityState: document.visibilityState || 'unknown',
                destination: '/taskdone/',
            });

            // Dismiss inactivity modal if open — clock expiry takes precedence
            if (wasIdle) {
                this.$logger.info(this.SERVICE, 'countdownExpiredJournal -> dismissing inactivity idle state');
                inactivityService.dismissIdle();
            }

            this.$rootScope.$safeApply(() => {
                this.$location.path('/taskdone/' + this.$bridges.authentication().getUserUUID());
            });
        };
        this.$signals.on('countdownExpiredJournal', onTimeExpiredJournal);

        this.FocusRecoveredService.init();

        this.redirectsBound = true;
    }
}

export default TaskFlowService;
